import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, ArrowRight, ShieldCheck } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/boss/home');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden bg-boss-surface-2 px-4">
      {/* Soft decorative background blobs — pure CSS, no image assets */}
      <div className="pointer-events-none absolute -top-32 -left-32 w-96 h-96 rounded-full bg-boss-violet-soft blur-3xl opacity-70" />
      <div className="pointer-events-none absolute -bottom-40 -right-24 w-96 h-96 rounded-full bg-boss-info-soft blur-3xl opacity-60" />

      <div className="relative w-full max-w-md bg-boss-surface rounded-3xl shadow-xl border border-boss-border px-8 py-10 sm:px-10 sm:py-12">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img src="/hg-logo-crop.png" alt="Himalaya Global" className="w-24 h-16 object-contain" />
        </div>

        <h1 className="text-2xl font-bold text-boss-text text-center">Sign in to your workspace</h1>
        <p className="text-sm text-boss-text-muted text-center mt-2 mb-8 leading-relaxed">
          Enter your BoSS credentials to pick up right where you left off.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-xs font-semibold text-boss-text-muted tracking-wide uppercase mb-2">
              Email
            </label>
            <div className="flex items-center gap-2 rounded-xl border border-boss-border bg-boss-surface-2 px-4 py-3 focus-within:ring-2 focus-within:ring-boss-violet/30 focus-within:border-boss-violet transition-colors">
              <Mail size={18} className="text-boss-text-muted shrink-0" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent text-sm text-boss-text placeholder:text-boss-text-muted/60 focus:outline-none"
                placeholder="you@company.com"
                data-testid="login-email"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-boss-text-muted tracking-wide uppercase mb-2">
              Password
            </label>
            <div className="flex items-center gap-2 rounded-xl border border-boss-border bg-boss-surface-2 px-4 py-3 focus-within:ring-2 focus-within:ring-boss-violet/30 focus-within:border-boss-violet transition-colors">
              <Lock size={18} className="text-boss-text-muted shrink-0" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent text-sm text-boss-text placeholder:text-boss-text-muted/60 focus:outline-none"
                placeholder="Enter your password"
                data-testid="login-password"
              />
            </div>
          </div>

          {error && (
            <div className="text-sm text-boss-danger bg-boss-danger-soft px-4 py-2.5 rounded-lg" data-testid="login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-boss-sidebar text-white font-semibold hover:bg-boss-sidebar-2 transition-colors disabled:opacity-50"
            data-testid="login-submit"
          >
            {loading ? 'Signing in...' : (
              <>
                Sign In
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>

        <div className="mt-6 flex items-center justify-center gap-1.5 text-xs text-boss-text-muted">
          <ShieldCheck size={14} className="text-boss-success" />
          <span>Secure login · Your data stays private</span>
        </div>

        <div className="mt-8 pt-6 border-t border-boss-border flex items-center justify-center gap-3 flex-wrap">
          {/* office-sahayogi-logo.png is a full lockup (mascot + wordmark) on a
              mostly-empty square canvas — shrinking it to an inline icon size
              renders as an illegible blur, so the credit line stays text-only.
              The mascot itself appears separately, cropped tight, in the page
              corner below (office-sahayogi-mascot.png). */}
          <span className="text-xs font-semibold" style={{ color: '#03255B' }}>Powered by Office Sahayogi</span>
          <span className="text-[11px] text-boss-text-muted/70">© 2026 Himalaya Global</span>
        </div>
      </div>

      <img
        src="/office-sahayogi-mascot.png"
        alt="Office Sahayogi"
        className="pointer-events-none select-none absolute bottom-5 right-6 h-20 w-auto object-contain drop-shadow-lg"
      />
    </div>
  );
}
